import React, { useMemo, useState } from 'react'
import tw from 'twin.macro'
import { CgSearch } from 'react-icons/cg'
import { orderBy } from 'lodash'

import { Button } from 'common/components/Button/Button'
import { EmptyStateContainer } from 'common/components/EmptyStateContainer/EmptyStateContainer'
import { Input } from 'common/components/Input/Input'
import { InputGroup } from 'common/components/Input/InputGroup'
import { Skeleton } from 'common/components/Skeleton/Skeleton'

import { useFetchCommunityMemberEmailsQuery, useFetchCommunityMemberProfilesQuery } from '../communityApi'
import PostAvatar from './PostAvatar'

// How many rows the list shows before the coach has to ask for more. A ban list
// is usually a handful of people, but nothing stops it growing, and the search
// box is the real answer to a long one.
const PAGE_SIZE = 50

// The moderation page's list of banned members, each with an Unban button.
//
// Display names are not unique in the app, so every row carries the member's
// email under the name, and the search box looks at both. That is also why all
// the names and emails are read up front rather than one row at a time: sorting
// and searching need the whole list in hand.
//
// `bannedIds` is the list of banned user ids, or undefined while it is loading.
// `onUnban` receives `{ kind: 'unban', userId, name }` and opens the confirmation.
export default function BannedMembers({ coachOrgId, bannedIds, onUnban }) {
  const [search, setSearch] = useState('')
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  const listLoading = bannedIds === undefined
  const hasBans = !listLoading && bannedIds.length > 0
  // Sorted so the same set of people is always the same request, whatever order
  // the database hands the ids back in.
  const sortedIds = useMemo(() => [...(bannedIds || [])].sort(), [bannedIds])

  const { data: profiles } = useFetchCommunityMemberProfilesQuery(
    { coachOrgId, userIds: sortedIds },
    { skip: !coachOrgId || !hasBans }
  )
  // Every member's email in one read, shared with the member card in the feed.
  const { data: emails, isError: emailsFailed } = useFetchCommunityMemberEmailsQuery(
    { coachOrgId },
    { skip: !coachOrgId || !hasBans }
  )
  // If the emails cannot be read, the list still has to work: a coach must be
  // able to unban someone. The rows then just have no email line.
  const emailsLoading = emails === undefined && !emailsFailed
  const detailsLoading = hasBans && (profiles === undefined || emailsLoading)

  const members = useMemo(() => {
    if (!hasBans || detailsLoading) return []
    const rows = sortedIds.map((userId) => ({
      userId,
      profile: profiles?.[userId] || null,
      name: profiles?.[userId]?.displayName || 'Unknown member',
      email: emails?.[userId] || '',
    }))
    return orderBy(rows, [(m) => m.name.toLowerCase(), (m) => m.email.toLowerCase()])
  }, [hasBans, detailsLoading, sortedIds, profiles, emails])

  const query = search.trim().toLowerCase()
  const matches = query
    ? members.filter((m) => m.name.toLowerCase().includes(query) || m.email.toLowerCase().includes(query))
    : members
  const visible = matches.slice(0, visibleCount)
  const hiddenCount = matches.length - visible.length

  const handleSearchChange = (e) => {
    setSearch(e.target.value)
    // A new search starts from the top of its own results.
    setVisibleCount(PAGE_SIZE)
  }

  if (listLoading || detailsLoading) {
    return <MemberRowSkeleton />
  }

  if (!hasBans) {
    return <EmptyStateContainer text='No banned members' />
  }

  return (
    <>
      <InputGroup css={[tw`mb-4 ring-offWhite`, search.length > 0 && tw`ring-tGreen ring-2`]}>
        <CgSearch tw='w-5 h-5' />
        <Input
          type='text'
          placeholder='Search by name or email'
          aria-label='Search banned members by name or email'
          onChange={handleSearchChange}
          value={search}
          hideRing
        />
        {search.length > 0 && (
          <Button variant='secondary' size='md' onClick={() => handleSearchChange({ target: { value: '' } })}>
            Clear
          </Button>
        )}
      </InputGroup>

      {visible.map((member) => (
        <BannedMemberRow key={member.userId} member={member} showEmail={!emailsFailed} onUnban={onUnban} />
      ))}

      {matches.length === 0 && <EmptyStateContainer text={`No banned members match “${search.trim()}”`} />}

      {hiddenCount > 0 && (
        <div className='flex justify-center mt-4'>
          <Button variant='secondary' size='md' onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}>
            Show {Math.min(hiddenCount, PAGE_SIZE)} more
          </Button>
        </div>
      )}
    </>
  )
}

function BannedMemberRow({ member, showEmail, onUnban }) {
  const { userId, name, email, profile } = member

  return (
    <div className='flex items-center bg-white rounded-2xl p-4 mb-3'>
      <PostAvatar name={name} picture={profile?.picture} color={profile?.color} size={36} />
      <div className='ml-3 min-w-0'>
        <p className='font-semibold text-tBlack truncate' data-testid='banned-member-name'>
          {name}
        </p>
        {showEmail && <p className='text-sm text-gray-500 truncate'>{email || 'No email on file'}</p>}
      </div>
      <button
        className='ml-auto shrink-0 px-3 py-1.5 text-sm font-semibold text-tGray-dark hover:text-tGreen'
        onClick={() => onUnban({ kind: 'unban', userId, name })}
      >
        Unban
      </button>
    </div>
  )
}

function MemberRowSkeleton() {
  return (
    <div className='flex items-center bg-white rounded-2xl p-4 mb-3'>
      <Skeleton className='!w-9 !h-9 shrink-0 rounded-full' />
      <Skeleton className='w-40 ml-3' />
    </div>
  )
}
